import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { searchEntitiesAction } from 'store/searchResult/searchResultActions';
import { EntitiesTable } from 'modules/common';
import { artifactsParser } from 'shared/parsers';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { TabsPanel, Artifacts } from 'ui';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { fetchArtifacts } from 'services';
import { ratingCoverageTabs } from 'shared/constants/ratingCoverageTabs';
import {
  getAllIndustriesData,
  getRelatedIndustries,
  getSortedFilterableIndustries,
  getCurrentIndustry,
} from 'selectors/common';

const page = 1;

const RatingCoverageIndustry = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { t } = useTranslation();
  const { code, industryCode, tab } = qs.parse(search.slice(1));
  const entities = useSelector(
    ({ searchResult }) => searchResult.entities.byRatingType[ratingTypes.CREDIT_RATING],
  );
  const [initialFilterValues, setInitialFilterValues] = useState(null);
  const allIndustriesData = useSelector(getAllIndustriesData);
  const relatedIndustries = useSelector(getRelatedIndustries);
  const filterableIndustries = useSelector(getSortedFilterableIndustries);
  const currentIndustry = useSelector(getCurrentIndustry(code, industryCode));
  const [isResearchPending, setIsResearchPending] = useState(false);
  const [researchArtifacts, setResearchArtifacts] = useState({ data: [], total: 0 });
  const [activeTab, setActiveTab] = useState(tab || ratingCoverageTabs.issuers);

  const initialFilter = { industrySectors: [{ sector: code, industry: industryCode }] };
  const changeTab = (_, value) => {
    setActiveTab(value);

    const queryParams = qs.parse(search.slice(1));
    const updatedParams = {
      ...queryParams,
      tab: value,
    };

    navigate({
      search: `?${qs.stringify(updatedParams)}`,
    });
  };

  const getArtifacts = async (industry) => {
    try {
      setIsResearchPending(true);
      const { data, total } = await fetchArtifacts({
        page,
        limit: 10,
        type: 'INDUSTRY_RESEARCH',
        industryCode: industry.code,
      });

      setResearchArtifacts({ data: artifactsParser(data), total });
    } finally {
      setIsResearchPending(false);
    }
  };

  const handleItemClick = (itemId) => {
    const path = `${pathname}/announcements/${itemId}`;
    navigate({ pathname: path, prevPath: pathname });
  };

  useEffect(() => {
    if (!allIndustriesData.length || !relatedIndustries.length) {
      return;
    }

    if (currentIndustry) {
      setInitialFilterValues({ industries: [currentIndustry] });

      getArtifacts(currentIndustry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allIndustriesData, relatedIndustries]);

  return (
    <>
      <Box mb={2}>
        <Typography variant="h5">{t('Your selected entities')}</Typography>
      </Box>
      <TabsPanel active={activeTab} onChange={changeTab}>
        <div label={t('Entities')} counter={entities.total} value="issuers">
          <EntitiesTable
            dataSelector={entities}
            actionCreator={searchEntitiesAction}
            constantFilter={{ withRatingOnly: true }}
            initialFilter={initialFilter}
            initialFilterModalValues={initialFilterValues}
            filterableIndustries={filterableIndustries}
            allowEmptyIndustriesFilter={false}
          />
        </div>
        <div label={t('Research')} counter={researchArtifacts.total} value="research">
          <Box p={2} mt={2}>
            <Artifacts
              data={researchArtifacts.data}
              isPending={isResearchPending}
              onItemClick={handleItemClick}
              currentPage={page}
            />
          </Box>
        </div>
      </TabsPanel>
    </>
  );
};

export { RatingCoverageIndustry };
